// External Imports
import { Fragment, useEffect, useState } from "react";
import { MdClose } from "react-icons/md";

// Internal Imports
import { requestAdminAPI } from "../../api/posh-api";
import uploadFile from "../../util/upload-file";
import Heading from "../UI/Heading";
import ImageCarousel from "../UI/ImageCarousel";
import LoadingSpinner from "../UI/LoadingSpinner";
import DetailPair from "../UI/DetailPair";
import SelectPair from "../UI/SelectPair";

/** ========================================================================
 * * StartBooking Component
 * Renders a form, which allows changing a booking status to started.
 * ========================================================================== */

export default function StartBooking({ setOpen, booking, onComplete }) {
  // Initialize States
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(false);
  const [item, setItem] = useState();

  /* ======================= FETCH VEHICLE STATS ======================= */
  const fetchVehicleStats = async () => {
    try {
      setLoading(true);
      // Send Request
      const response = await requestAdminAPI("fetch-vehicle-reports", {
        vehicles: [
          { vin: booking.vehicle.vin, trackerId: booking.vehicle.trackerId },
        ],
      });
      const report = response[0]?.report;
      if (report) {
        document.getElementById("mileage").value = report.mileage;
        document.getElementById("fuel").value = report.fuel;
      }
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };
  /* ======================= START/END BOOKING ======================= */
  const confirmHandler = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      // Get Image URLs
      const imageUrls = [];
      /*
       * Upload images directly to the database
       * and get the URL of the uploaded images.
       */
      if (images.length > 0) {
        const path = `bookings/${booking.id}/checkInImages`;
        await Promise.all(
          images.map(async (image, index) => {
            setItem(image.name);
            // Get File Extension
            const extension = image.name.split(".").pop();
            const type = image.type.split("/")[0];
            const url = await await uploadFile(
              `${path}_${new Date().toISOString()}_${index + 1}-${type}.${extension}`,
              image,
              setUploadProgress
            );
            imageUrls.push(url);
          })
        );
      }

      // Create Payload
      const payload = {
        bookingId: booking.id,
        checkInImages: imageUrls,
        startMileage: document.getElementById("mileage").value,
        startFuel: document.getElementById("fuel").value,
        start: document.getElementById("start").value,
      };

      // Send Request
      await requestAdminAPI("start-booking", payload);
      setLoading(false);
      setOpen(false);
      onComplete && onComplete();
    } catch (err) {
      console.error(err);
      setLoading(false);
      setUploadProgress(false);
    }
  };

  useEffect(() => {
    fetchVehicleStats();
  }, []);

  return (
    <Fragment>
      <Heading
        title="Start Booking"
        text="IMPORTANT: Customers need to check in regardless of whether booking is started by an admin."
        isBackArrow={false}
      >
        <MdClose className="icon" onClick={() => setOpen(false)} />
      </Heading>

      <ImageCarousel images={images} setImages={setImages} preview={true} />

      <div className="info">
        <DetailPair
          label="Current Mileage (mi)"
          edit={true}
          inputOptions={{ id: "mileage" }}
        />
        <DetailPair
          label="Current Fuel Level (%)"
          edit={true}
          inputOptions={{ id: "fuel" }}
        />
        <SelectPair
          label="Start Booking"
          edit={true}
          options={[
            { id: false, name: "No - Await Customer" },
            { id: true, name: "Yes - Skip Agreement" },
          ]}
          inputOptions={{ id: "start" }}
        />
      </div>

      {loading ? (
        <div className="flex items-center space-x-4">
          <LoadingSpinner />
          {uploadProgress && (
            <p className="text-sm text-stone-400">
              Uploading {item}... {uploadProgress}%
            </p>
          )}
        </div>
      ) : (
        <div className="info">
          <button className="btn-primary" onClick={confirmHandler}>
            Submit
          </button>
        </div>
      )}
    </Fragment>
  );
}
